// FeedbackForm.tsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import api from "@/components/customAxios/Axios";
import { useLoader } from "@/context/loaderContext";
import { MdOutlineDone } from "react-icons/md";
import AssessmentSideBar from "@/components/AssessmentSideBar";
import AssessmentPagesFooter from "@/components/AssessmentPagesFooter";

const RenderStars = ({
	rating,
	setRating,
	onRatingSet,
}: {
	rating: number;
	setRating: React.Dispatch<React.SetStateAction<number>>;
	onRatingSet: () => void;
}) => {
	const [hover, setHover] = useState(0);

	return (
		<div className="flex space-x-9 justify-center mt-5">
			{[1, 2, 3, 4, 5].map((star) => (
				<div>
					<button
						key={star}
						type="button"
						onMouseEnter={() => setHover(star)} // Set hover state
						onMouseLeave={() => setHover(0)} // Reset hover state
						onClick={() => {
							setRating(star);
							onRatingSet();
						}} // Set rating and trigger next question
						className={`text-3xl ${
							hover >= star || rating >= star
								? "text-yellow-400"
								: "text-gray-400"
						}`}
					>
						<FontAwesomeIcon icon={faStar} />
					</button>
					{star == 1 && (
						<div className="text-gray-400 text-center mt-1 ">Worst</div>
					)}
					{star == 5 && (
						<div className="text-gray-400 text-center mt-1 ">Excellent</div>
					)}
				</div>
			))}
		</div>
	);
};

const RenderStep = ({
	question,
	value,
	setValue,
	onRatingSet,
}: {
	question: string;
	value: number;
	setValue: React.Dispatch<React.SetStateAction<number>>;
	onRatingSet: () => void;
}) => {
	return (
		<div className="space-y-4 ">
			<label className="block text-xl  font-body">{question}</label>
			<RenderStars
				rating={value}
				setRating={setValue}
				onRatingSet={onRatingSet}
			/>
		</div>
	);
};

const FeedbackForm = () => {
	const [readability, setReadability] = useState(0);
	const [questionDifficulty, setQuestionDifficulty] = useState(0);
	const [relevance, setRelevance] = useState(0);
	const [overallSatisfaction, setOverallSatisfaction] = useState(0);
	const [responsiveness, setResponsiveness] = useState(0);

	const [comments, setComments] = useState("");
	const [showQuestions, setShowQuestions] = useState(true);
	const [currentStep, setCurrentStep] = useState(0);
	const [showMessage, setShowMessage] = useState(false);
	const navigate = useNavigate();
	const { setLoading } = useLoader();

	const submitFeedbackForm = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			setLoading(true);
			const beha = sessionStorage.getItem("question");
			const candidateId = beha ? JSON.parse(beha).candidate_id : "";
			const response = await api.post("/save_candidate_feedack", {
				candidate_id: candidateId,
				feedback: {
					"1": readability,
					"2": questionDifficulty,
					"3": relevance,
					"4": overallSatisfaction,
					"5": responsiveness,
				},
				remark: comments,
			});
			if (response.data) {
				setShowMessage(true);

				setTimeout(() => {
					navigate("/success");
				}, 2000);
			} else navigate("/success");
		} catch (error) {
			navigate("/success");
		} finally {
			setLoading(false);
		}
	};

	const steps = [
		{
			question: "How readable was the content?",
			value: readability,
			setValue: setReadability,
		},
		{
			question: "How challenging were the questions?",
			value: questionDifficulty,
			setValue: setQuestionDifficulty,
		},
		{
			question: "How relevant were the questions to the topic?",
			value: relevance,
			setValue: setRelevance,
		},
		{
			question: "How satisfied were you with the overall experience?",
			value: overallSatisfaction,
			setValue: setOverallSatisfaction,
		},
		{
			question: "How responsive was the platform during the test?",
			value: responsiveness,
			setValue: setResponsiveness,
		},
	];
	const getStepStyles = (isActive: number) =>
		isActive ? "bg-[#438437] text-white" : "bg-[#BDE4C3] text-[#438437]";

	const getConnectorStyles = (isActive: number) =>
		isActive ? "bg-[#438437]" : "bg-[#BDE4C3]";
	const handleRatingSet = () => {
		if (currentStep === steps.length - 1) {
			setShowQuestions(false);
		} else {
			setCurrentStep((prev) => prev + 1);
		}
	};

	return (
		<>
			<div className="min-h-[90vh] ">
				<AssessmentSideBar></AssessmentSideBar>
				<div className=" py-10 flex items-center justify-center ">
					{!showMessage ? (
						<form
							className="mx-auto p-8 bg-white dark:bg-gray-900 border rounded-2xl space-y-6 w-full max-w-lg h-[500px]"
							onSubmit={(e) => e.preventDefault()}
						>
							<div className="border-b-2">
								<h2 className="text-xl font-bold font-heading text-gray-800 dark:text-white mb-6">
									Rate your experience
								</h2>
							</div>

							<div className="flex justify-center items-center">
								{steps.map((step, index) => (
									<div
										key={index}
										className="flex items-center text-sm font-body"
									>
										{/* Step Indicator */}
										<span
											className={`flex items-center justify-center w-10 h-10 font-bold rounded-full ${getStepStyles(
												step.value
											)}`}
										>
											{step.value ? <MdOutlineDone /> : index + 1}
										</span>

										{/* Connector */}
										{index < steps.length - 1 && (
											<div
												className={`w-10 h-1 mx-1 ${getConnectorStyles(
													steps[index].value
												)}`}
											/>
										)}
									</div>
								))}
							</div>

							{showQuestions ? (
								<div className="">
									<p className="text-gray-500 mb-2 mt-5 font-subheading">
										Question {currentStep + 1}
									</p>
									<RenderStep
										question={steps[currentStep].question}
										value={steps[currentStep].value}
										setValue={steps[currentStep].setValue}
										onRatingSet={handleRatingSet}
									/>
								</div>
							) : (
								<div className="space-y-4">
									<label className="block text-lg font-medium text-gray-700 dark:text-gray-300">
										Additional Comments:
									</label>
									<textarea
										value={comments}
										onChange={(e) => setComments(e.target.value)}
										placeholder="Share your thoughts..."
										rows={6}
										className="w-full p-4 border border-gray-300 dark:border-gray-700 rounded-lg "
									/>
								</div>
							)}

							<div className="flex justify-between items-center mt-6">
								<div className="flex justify-between">
									{!showQuestions && (
										<button
											type="submit"
											className="bg-[#BDE4C3] hover:scale-110 transition text-[#438437] py-2 px-6 rounded-lg "
											onClick={submitFeedbackForm}
										>
											Submit Feedback
										</button>
									)}
								</div>

								<button
									className=" text-[#438437] font-medium hover:underline focus:outline-none mt-2"
									onClick={() => navigate("/success")}
								>
									Skip
								</button>
							</div>
						</form>
					) : (
						<div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-opacity-60 z-50">
							<div className="bg-white dark:bg-gray-900 rounded-xl p-6 md:p-10 shadow-lg max-w-lg">
								<div className="flex flex-col items-center text-center">
									<div className="w-16 h-16 flex items-center justify-center bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 rounded-full mb-6">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth={2}
											stroke="currentColor"
											className="w-8 h-8"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</div>
									<h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-4 font-heading">
										We Appreciate Your Feedback!
									</h2>
									<p className="text-gray-600 dark:text-gray-400 mb-6 font-subheading">
										Thank you for taking the time to share your thoughts with
										us. Your feedback helps us improve and deliver a better
										experience for everyone.
									</p>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
			<AssessmentPagesFooter />
		</>
	);
};

export default FeedbackForm;
